import React, { useRef, useState, useCallback, useEffect } from 'react'

import {
    Box,
    Button,
    Tooltip,
    Link
} from '@mui/material';

import { grey } from '@mui/material/colors';

import { useNavigate } from 'react-router-dom';
// import { createTheme, ThemeProvider } from '@mui/material/styles';

import DateFilter from '@inovua/reactdatagrid-community/DateFilter'
import { CommonIcons, icons } from '../../common/CommonIcons';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonRestricted from '../../common/CommonRestricted';
import DataGridToolbar from '../../common/DataGridToolbar';
import DataGridTable from '../../common/DataGridTable';
import DataGridRowActionMenu from '../../common/DataGridRowActionMenu';
import { useAppContextFeatures } from '../../../context/AppContext';

import { useDataGridParams } from '../../../hooks/useDataGridParams';
import { useDocuments, documentTypes, targetTypes } from '../../../hooks/api/useDocuments';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useDependencies, dependencyModules, targetTypes as dependencyTargetTypes } from '../../../hooks/api/useDependencies';

import { useTranslations } from "../../../hooks/useTranslations";

export default function ListTripOrders({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "prompt", "@logistic", "@document"] })

    const navigate = useNavigate();
    const { promptMessage } = useSystemPrompt();
    const { list, remove } = useDocuments({ documentType: documentTypes.tripOrder, targetType: targetTypes.tripOrder });
    const { RenderDatagridDisplay: RenderDependencyDatagridDisplay } = useDependencies()

    const { defineColumns, columns, filterValues, getRenderColumnLink, getRenderColumnTextDisplay } = useDataGridParams('id')
    const [selectedIds, setSelectedIds] = useState([])
    const [refreshDataGrid, setRefreshDataGrid] = useState(0)
    const { getLocationIds } = useAppContextFeatures();

    const gridRef = React.createRef();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.document.id, scopeTypes.document.tripOrder.id)

    const resetDataGridTableSelection = () => {
        setSelectedIds([])
    }

    useEffect(() => {

        setRefreshDataGrid(val => val + 1)

    }, [getLocationIds])

    const fetchData = async (props) => {

        resetDataGridTableSelection();

        try {
            props.dataTypes = ["dependency"]

            props.customFilterLocationEnable = true;
            props.customFilterLocationFields = ['location_id']

            const result = await list(props)

            let rawData = result?.data?.documents

            let newData = []

            rawData.forEach(i => {

                let _i = i

                let outward = _i?.outward_dependencies
                let inward = _i?.inward_dependencies

                let all = [...outward, ...inward]

                // declare a value and allocate the combined data
                _i.all_dependencies = all

                newData.push(_i)
            })

            return {
                data: newData,
                count: result?.data?.total
            }

        } catch (error) {

            promptMessage(false, `Error getting results (${error?.message})`)

            return {
                data: [],
                count: 0
            }
        }

    }

    useEffect(() => {

        defineColumnsView()

    }, [])

    const defineColumnsView = () => {
        defineColumns([
            {
                name: 'location_name', header: t('location')
            },
            {
                name: 'doc_number',
                header: t('docNo'),
                renderValue: ({ value, cellProps }) => {

                    if (isPermissionAllow({ functionType: functionTypes.pageView.id }) === false) {
                        return getRenderColumnTextDisplay(value)
                    }

                    let path = getRoute(`documents/tripOrder/view/${cellProps.data.id}`);

                    return getRenderColumnLink(path, value)

                }
            },
            {
                name: 'doc_date', header: t('date')
            },
            {
                name: 'driver_name', header: t('driver')
            },
            {
                name: 'logistic_label', header: t('logistic')
            },
            {
                name: 'doc_remark', header: t('remark')
            },
            {
                name: 'docs',
                header: t('destination'),
                renderValue: ({ value, cellProps }) => {

                    const docsLength = cellProps.data.docs.map(i => i.docs === "1")

                    return (
                        <Box
                            display={"flex"}
                            flexDirection={"row"}
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                        >
                            <Tooltip
                                placement="right"
                            >
                                <Box display={"flex"} flexGrow={"1"} flexDirection={"row"} justifyContent={"flex-start"} alignItems={"center"}>
                                    <CommonIcons name={icons.destination} />
                                    <Box sx={{ ml: 1 }}>
                                        {docsLength.length}
                                    </Box>
                                </Box>

                            </Tooltip>

                        </Box>
                    )

                }
            },
            {
                name: 'is_complete_status',
                header: t('completion'),
                renderValue: ({ value, cellProps }) => {

                    const isConfirmStatus = Boolean(cellProps.data.is_confirm_status)

                    if (isConfirmStatus === false) {
                        return (
                            <Box
                                display={"flex"}
                                flexDirection={"row"}
                                justifyContent={"flex-start"}
                                alignItems={"center"}
                            >
                                <Tooltip
                                    title={"Pending"}
                                    placement="right"
                                >
                                    <Button
                                        size='small'
                                        variant={"outlined"}
                                        color={`primary`}
                                        sx={{
                                            textAlign: "center",
                                            width: 100
                                        }}
                                        onClick={() => {

                                            if (isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) === false) {
                                                navigate(getRoute(`documents/tripOrder/update/${cellProps.data.id}`))
                                            }

                                        }}
                                    >
                                        {t('pending')}
                                    </Button>

                                </Tooltip>

                            </Box>
                        )
                    }

                    const done = cellProps.data.docs.reduce((total, i) => i.is_complete_status === "1" ? total + 1 : 0, 0)
                    const total = cellProps.data.docs.length

                    return (
                        <Box
                            display={"flex"}
                            flexDirection={"row"}
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                        >
                            <Tooltip
                                title={
                                    done === total ? t("completed") : t("incomplete")
                                }
                                placement="right"
                            >
                                <Button
                                    size='small'
                                    variant={done === total ? "contained" : "outlined"}
                                    color={`primary`}
                                    sx={{
                                        textAlign: "center",
                                        width: 100
                                    }}
                                    startIcon={<CommonIcons name={icons.logistic} />}
                                >
                                    {done} / {total}

                                </Button>

                            </Tooltip>

                        </Box>
                    )

                }
            },
            {
                name: 'all_dependencies',
                header: t('relatedDocs'),
                renderValue: ({ value, cellProps }) => {

                    return (
                        <RenderDependencyDatagridDisplay
                            id={cellProps.data.id}
                            values={value}
                            renderValue={({ id, otherUrl }) => {

                                if (otherUrl) {
                                    return getRoute(otherUrl)
                                }

                                return getRoute(`documents/tripOrder/view/${id}`)
                            }}
                        />
                    )

                }

            },
            {
                name: 'is_complete_date', header: t('completed')
            },
        ])
    }

    const handleDelete = async () => {

        try {

            let result = await remove({ ids: selectedIds, targetType: targetTypes.trip })

            promptMessage(true, result?.message)
            setRefreshDataGrid(val => val + 1)

        } catch (error) {
            promptMessage(false, error?.message)
        }

    }

    const RenderToolbar = () => {

        const getToolbarMainMenus = () => {

            let props = [];

            if (isPermissionAllow({ functionType: functionTypes.pageNew.id })) {
                props.push({
                    displayIconName: icons.plus,
                    onClick: () => {

                        navigate(getRoute("documents/tripOrder/new"));

                    }
                })
            }

            return props
        }

        const getActionOnClick = () => {

            let handleFunction = null

            if (isPermissionAllow({ functionType: functionTypes.remove.id })) {
                handleFunction = handleDelete
            }

            return handleFunction
        }

        return (
            <DataGridToolbar
                selectedRowCount={
                    selectedIds.length
                }
                mainMenus={
                    getToolbarMainMenus()
                }
                actionOnClickRemove={
                    getActionOnClick()
                }
                actionMenus={[]}
            />
        )
    }

    const RenderDataGridTable = () => {

        return (
            <DataGridTable
                ref={gridRef}
                refresh={refreshDataGrid}
                fetchData={fetchData}
                dataGrid={{
                    idProperty: "id",
                    style: {
                        minHeight: 600
                    },
                    pageSizes: [20, 50, 100],
                    defaultLimit: 10,
                    defaultSkip: 0,
                    checkboxColumn:
                        isPermissionAllow({ functionType: functionTypes.remove.id }),
                    columns,
                    defaultFilterValue: filterValues,
                    onSelectionChange: (items) => {
                        // console.log('items selected : ', items)
                        setSelectedIds(
                            items
                        )
                    }
                }}

            />
        )

    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.deliveryTrips })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("tripOrders")}
                breadcrumbs={[
                    {
                        displayText: t("tripOrders"),
                        props: {
                            // underline: "hover",
                            // href: "/"
                        }
                    }
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageList.id }) ?
                    <>
                        {
                            RenderToolbar()
                        }
                        {
                            RenderDataGridTable()
                        }
                    </>
                    :
                    (
                        <CommonRestricted />
                    )
            }

        </Box>
    )
}
